import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Card, Typography, Space, Alert, Button, Descriptions, Tag, Spin, message,
} from 'antd';
import {
  ReloadOutlined, ApiOutlined, CheckCircleOutlined, CloseCircleOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { trendsAPI, systemConfigAPI } from '../services/api';

const { Title, Text, Paragraph } = Typography;

const TRENDS_KEYS = [
  'TRENDS_RESEARCH_ENABLED',
  'BRAVE_SEARCH_API_KEY',
  'SCRAPECREATORS_API_KEY',
  'OPENROUTER_API_KEY',
  'LAST30DAYS_MEMORY_DIR',
];

export default function TrendsSettingsPage() {
  const [health, setHealth] = useState(null);
  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [healthRes, configsRes] = await Promise.all([
        trendsAPI.health(),
        systemConfigAPI.list(),
      ]);
      setHealth(healthRes.data ?? healthRes);
      const all = (configsRes.data ?? configsRes) || [];
      const filtered = {};
      for (const c of all) {
        if (TRENDS_KEYS.includes(c.key)) filtered[c.key] = c;
      }
      setConfigs(filtered);
    } catch (e) {
      message.error(`載入失敗：${e.message}`);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          <SettingOutlined /> 來源設定 — last30days-skill
        </Title>
        <Text type="secondary">查看 skill 安裝狀態 / 各 API key 設定 / 安裝指引</Text>
      </div>

      {/* Health */}
      <Card title={<><ApiOutlined /> 健康狀態</>} style={{ marginBottom: 16 }}
            extra={<Button size="small" icon={<ReloadOutlined />} onClick={fetchAll} loading={loading}>重新檢查</Button>}>
        {!health ? <Spin /> : (
          <Alert
            type={health.installed ? 'success' : 'warning'}
            showIcon
            icon={health.installed ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
            message={health.installed ? 'skill 已安裝並可用' : 'skill 尚未安裝 / 不可用'}
            description={
              <div>
                {health.error && <div>錯誤：{health.error}</div>}
                {health.hint && <div>提示：{health.hint}</div>}
                {health.installed && health.version && <div>版本：{health.version}</div>}
              </div>
            }
          />
        )}
      </Card>

      {/* Keys */}
      <Card title="API Keys（在 alayaess host 用，SPO 只負責顯示狀態 + 提醒）" style={{ marginBottom: 16 }}>
        <Alert
          type="info" showIcon style={{ marginBottom: 16 }}
          message={<>
            這些 Key 真正消費發生在 alayaess host 的 skill 程式裡。SPO 在<Link to="/ai-config">「AI 模型設定」</Link>頁有對應欄位可以填，
            填了之後同步給 alayaess（這部分需要另外的同步機制 — 見下方安裝指引）。
          </>}
        />
        <Descriptions column={1} bordered size="small">
          {TRENDS_KEYS.map(k => {
            const c = configs[k];
            const hasValue = c && c.value && c.value.length > 0;
            return (
              <Descriptions.Item key={k} label={
                <Space>
                  <code>{k}</code>
                  {hasValue ? <Tag color="success">已設定</Tag> : <Tag>未設定</Tag>}
                </Space>
              }>
                <Text type="secondary">{c?.description || '—'}</Text>
                {hasValue && k !== 'LAST30DAYS_MEMORY_DIR' && (
                  <div style={{ fontFamily: 'monospace', fontSize: 12, marginTop: 4 }}>
                    {c.value}
                  </div>
                )}
                {!hasValue && k !== 'TRENDS_RESEARCH_ENABLED' && k !== 'LAST30DAYS_MEMORY_DIR' && (
                  <div style={{ marginTop: 4 }}>
                    <Link to="/ai-config">→ 到 AI 模型設定填 key</Link>
                  </div>
                )}
              </Descriptions.Item>
            );
          })}
        </Descriptions>
      </Card>

      {/* Install guide */}
      <Card title="安裝指引（alayaess host 上）">
        <Paragraph>
          last30days-skill 跑在 alayaess host（<code>root@10.66.99.82</code>），不是跑在 SPO container 內。
          請在 alayaess host 跑下列指令安裝：
        </Paragraph>

        <Title level={5}>1. 安裝 skill</Title>
        <pre style={preStyle}>{`# 用 clawhub 裝（建議）
ssh root@10.66.99.82
clawhub install last30days-official

# 或從 source
git clone https://github.com/mvanhorn/last30days-skill.git ~/last30days-skill
ln -s ~/last30days-skill/skills/last30days ~/.claude/skills/last30days`}</pre>

        <Title level={5}>2. 安裝依賴</Title>
        <pre style={preStyle}>{`brew install yt-dlp  # YouTube transcripts
pip install -r ~/last30days-skill/requirements.txt  # Python 3.12+`}</pre>

        <Title level={5}>3. 設定環境變數（在 alayaess host）</Title>
        <pre style={preStyle}>{`# 寫進 ~/.bashrc 或對應的 systemd service file
export BRAVE_SEARCH_API_KEY="..."           # 必要
export SCRAPECREATORS_API_KEY="..."         # TikTok/IG/Threads/Pinterest
export OPENROUTER_API_KEY="..."             # Perplexity Sonar
export LAST30DAYS_MEMORY_DIR=/data/last30days`}</pre>

        <Title level={5}>4. 在 openclaw-relay 加 bridge endpoint</Title>
        <Paragraph>
          需要在 <code>/root/openclaw-relay/server.js</code> 加兩個 endpoint：
          <code>POST /api/v1/skills/last30days/run</code> 跟 <code>GET /api/v1/skills/last30days/file</code>。
          這支 SPO 安裝包提供現成的 JS 片段；請在 alayaess 那邊跑：
        </Paragraph>
        <pre style={preStyle}>{`# 完整 install 範本在 docs/alayaess-relay-snippets.md，
# 由 SPO 工程師寄給 alayaess 維護者，或開另一個 Claude Code session 自動裝。`}</pre>

        <Alert
          type="info" showIcon style={{ marginTop: 12 }}
          message="貼心提醒"
          description={<>
            裝完後重新整理本頁，「健康狀態」會變成綠燈。然後就可以去 <Link to="/research/trends">「趨勢搜尋」</Link>頁開始跑研究，或直接在
            <Link to="/ai-sales"> AI 業務競賽</Link> 的 bot chat 用
            <code> [TRIGGER_TRENDS_RESEARCH topic="..."] </code> 觸發。
          </>}
        />
      </Card>
    </div>
  );
}

const preStyle = {
  background: '#1f2937', color: '#e5e7eb', padding: 12, borderRadius: 8,
  fontSize: 12, overflow: 'auto', marginBottom: 12,
};
